/**
 * Squares over odds: helpers to extract inputs from Decimal Over market (market_type_id 410).
 * Grid odds come from the backend only (POST /squares/calculate-over-odds): DB if locked, else generate+lock+persist.
 */

/** market_type_id for Decimal Over market used by Squares */
export const SQUARES_OVER_MARKET_TYPE_ID = "410";

/** Handicap lines we accept for Squares 410 (matches selectPreferredOverMarketForOver filter). */
const SQUARES_RUNS_LINE_MIN = 2.5;
const SQUARES_RUNS_LINE_MAX = 21.5;

function marketNameForSquares(marketOrName) {
  if (marketOrName != null && typeof marketOrName === "object") {
    return marketOrName.market_name ?? marketOrName.name ?? "";
  }
  return marketOrName ?? "";
}

function isPlausibleSquaresRunsLine(n) {
  return Number.isFinite(n) && n >= SQUARES_RUNS_LINE_MIN && n <= SQUARES_RUNS_LINE_MAX;
}

function lineFromMarketTypePrefix(marketType) {
  if (!marketType) return null;
  const m = String(marketType).match(/^(\d+\.?\d*)\s/);
  return m ? parseFloat(m[1]) : null;
}

function lineFromNameSuffix(name, overNum) {
  if (!name) return null;
  const m = String(name).match(/(\d+\.?\d*)\s*$/);
  if (!m) return null;
  const parsed = parseFloat(m[1]);
  // "Team Runs in Over 12" — trailing integer is the over index, not the handicap line.
  if (overNum != null && Number.isFinite(overNum) && parsed === overNum) return null;
  return parsed;
}

/**
 * Extract over number from market 410 name (e.g. "Team Runs in Over 8" -> 8).
 * Accepts a market object (`market_name` / `name` / `over`) or a raw name string.
 */
export function getRunsInOverNumber(nameOrMarket) {
  if (nameOrMarket != null && typeof nameOrMarket === "object") {
    const fromField =
      nameOrMarket.over != null && Number.isFinite(Number(nameOrMarket.over))
        ? Number(nameOrMarket.over)
        : null;
    if (fromField != null && fromField >= 1) return fromField;
  }
  const name = marketNameForSquares(nameOrMarket);
  if (!name || typeof name !== "string") return null;
  const m = name.match(/Runs in Over\s+(\d+)/i);
  return m ? parseInt(m[1], 10) : null;
}

/**
 * Extract line (P4) from market: price.line, market.line, market_type prefix, name suffix, then
 * market_line_id only when it looks like a handicap (not a feed row id).
 */
export function getOverLineFromMarket(market, price, overNum) {
  const p = price ?? market;
  const priceLine = p?.line != null && p.line !== "" ? parseFloat(p.line) : NaN;
  if (Number.isFinite(priceLine) && priceLine !== 0 && isPlausibleSquaresRunsLine(priceLine)) return priceLine;

  const marketLine = market?.line != null && market.line !== "" ? parseFloat(market.line) : NaN;
  if (Number.isFinite(marketLine) && marketLine !== 0 && isPlausibleSquaresRunsLine(marketLine)) return marketLine;

  const handicap = market?.handicap != null && market.handicap !== "" ? parseFloat(market.handicap) : NaN;
  if (Number.isFinite(handicap) && handicap !== 0 && isPlausibleSquaresRunsLine(handicap)) return handicap;

  const fromType = lineFromMarketTypePrefix(market?.market_type);
  if (isPlausibleSquaresRunsLine(fromType)) return fromType;

  const fromName = lineFromNameSuffix(marketNameForSquares(market), overNum);
  if (isPlausibleSquaresRunsLine(fromName)) return fromName;

  const lid = market?.market_line_id;
  if (lid != null && lid !== "") {
    const parsed = parseFloat(String(lid));
    if (isPlausibleSquaresRunsLine(parsed)) return parsed;
  }
  return null;
}

/**
 * Get Over selection decimal price (selection id 1) from market.
 * @param {object} market - Market object
 * @param {Map|object} marketPriceMap - Either a Map (key -> price) or a single price/market object to use when no Map
 */
export function getOverPriceFromMarket(market, marketPriceMap) {
  const getMarketKey = (p) => {
    const mid = p?.market_id != null ? String(p.market_id) : "";
    const lid = p?.market_line_id != null ? String(p.market_line_id) : "";
    if (mid && lid) return `${mid}_${lid}`;
    if (lid) return lid;
    return mid || "";
  };
  const price =
    marketPriceMap && typeof marketPriceMap.get === "function"
      ? marketPriceMap.get(getMarketKey(market)) || market
      : marketPriceMap || market;
  const sel = price?.selectionslist ?? price?.selections;
  const list = Array.isArray(sel) ? sel : [];
  const overSel =
    list.find((s) => String(s?.id) === "1" || String(s?.selection_id) === "1") ||
    list.find((s) => /^over\b/i.test(String(s?.name ?? s?.["selection-name"] ?? "").trim()));
  if (!overSel) return null;
  const recommended = parseFloat(overSel?.recommended_price);
  if (Number.isFinite(recommended) && recommended >= 1) return recommended;
  const odds = parseFloat(overSel?.odds);
  return Number.isFinite(odds) && odds >= 1 ? odds : null;
}

/**
 * Over index (1-based) for a 410 market: `over` field when set, else parsed from name.
 */
export function overNumberFromSquaresMarket(m) {
  const overNum = getRunsInOverNumber(m);
  if (overNum == null || !Number.isFinite(overNum) || overNum < 1) return null;
  return overNum;
}

/**
 * Set of 1-based over indices present in Squares Decimal Over (410) markets (from stream `market_update.markets`).
 */
export function getSquares410OverNumbersFromMarkets(markets) {
  const overs = new Set();
  if (!Array.isArray(markets)) return overs;
  for (const m of markets) {
    if (String(m?.market_type_id) !== SQUARES_OVER_MARKET_TYPE_ID) continue;
    const n = overNumberFromSquaresMarket(m);
    if (n != null) overs.add(n);
  }
  return overs;
}

/**
 * Best lobby label over from 410 markets for Squares: only **current** and **next** over (not deeper futures).
 * - With `cricketCurrentOver` (1-based), prefers the smallest 410 over in `[current, current+1]` if present.
 * - Without anchor, falls back to the minimum 410 over in the payload (e.g. pre-match).
 * @param {Array|undefined} markets - `market_update.markets` from the events stream
 * @param {number|null|undefined} cricketCurrentOver - `Math.floor(full + part/6) + 1` from Current Innings Overs + Part Overs
 */
export function getPlayableSquaresOverFromMarkets(markets, cricketCurrentOver) {
  if (!Array.isArray(markets) || markets.length === 0) return null;

  const overs = new Set();
  for (const m of markets) {
    if (String(m?.market_type_id) !== SQUARES_OVER_MARKET_TYPE_ID) continue;
    const n = overNumberFromSquaresMarket(m);
    if (n != null) overs.add(n);
  }
  if (overs.size === 0) return null;

  const sorted = [...overs].sort((a, b) => a - b);
  if (cricketCurrentOver != null && Number.isFinite(cricketCurrentOver)) {
    const cur = cricketCurrentOver;
    const inWindow = sorted.filter((n) => n >= cur && n <= cur + 1);
    if (inWindow.length > 0) return inWindow[0];
    const ge = sorted.find((n) => n >= cur);
    if (ge != null) return ge;
  }
  return sorted[0];
}

/**
 * Pick preferred 410 market for a given over:
 * - consider all markets for that over
 * - keep only lines in [2.5, 21.5]
 * - choose price closest to 2.0 (deterministic tie-break: lower line first)
 */
function squares410MarketCandidates(markets, marketPriceMap, overNumFilter) {
  return (markets || [])
    .filter((m) => {
      if (String(m?.market_type_id) !== SQUARES_OVER_MARKET_TYPE_ID) return false;
      if (overNumFilter == null) return true;
      return overNumberFromSquaresMarket(m) === overNumFilter;
    })
    .map((market) => {
      const priceObj =
        marketPriceMap && typeof marketPriceMap.get === "function"
          ? marketPriceMap.get(
              (() => {
                const mid = market?.market_id != null ? String(market.market_id) : "";
                const lid = market?.market_line_id != null ? String(market.market_line_id) : "";
                if (mid && lid) return `${mid}_${lid}`;
                if (lid) return lid;
                return mid || "";
              })()
            ) || market
          : market;
      const overNum = overNumberFromSquaresMarket(market);
      const line = getOverLineFromMarket(market, priceObj, overNum);
      const price = getOverPriceFromMarket(market, marketPriceMap);
      return { market, line, price };
    })
    .filter((x) => Number.isFinite(x.line) && x.line >= 2.5 && x.line <= 21.5 && Number.isFinite(x.price) && x.price >= 1);
}

function pickPreferredSquares410Candidate(candidates) {
  if (!candidates.length) return null;
  candidates.sort((a, b) => {
    const d = Math.abs(a.price - 2.0) - Math.abs(b.price - 2.0);
    if (d !== 0) return d;
    return a.line - b.line;
  });
  return candidates[0];
}

/**
 * Pick preferred 410 market for a given over:
 * - consider all markets for that over
 * - keep only lines in [2.5, 21.5]
 * - choose price closest to 2.0 (deterministic tie-break: lower line first)
 */
export function selectPreferredOverMarketForOver(markets, overNum, marketPriceMap) {
  return pickPreferredSquares410Candidate(squares410MarketCandidates(markets, marketPriceMap, overNum));
}

/**
 * Any playable 410 line (not tied to an over). Used at innings 0.0 / chase Over 1 when
 * Decimal still publishes leftover prior-over 410s until it lists Over 1 of the new innings.
 */
export function selectPreferredOverMarketAnyOver(markets, marketPriceMap) {
  return pickPreferredSquares410Candidate(squares410MarketCandidates(markets, marketPriceMap, null));
}

/** Over-specific 410, else any leftover 410 when generating Over 1 at innings 0.0. */
export function selectSquares410MarketForOddsGrid(markets, overNum, marketPriceMap, { atInningsOverZero = false } = {}) {
  const exact = selectPreferredOverMarketForOver(markets, overNum, marketPriceMap);
  if (exact) return exact;
  if (atInningsOverZero && Number(overNum) === 1) {
    return selectPreferredOverMarketAnyOver(markets, marketPriceMap);
  }
  return null;
}
